import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "./Auth/Signin";
import AuthMiddleware from "./middleware/authMiddleware";
import Layout from "./Layout/Layout";
import AnimationThemeProvider from "./_helper/AnimationTheme/AnimationThemeProvider";
import { colorsTuple, createTheme, MantineProvider } from "@mantine/core";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RecoilRoot } from "recoil";
import { Toaster } from "sonner";
import CustomizerProvider from "./_helper/Customizer/CustomizerProvider";
import { routes as authenticatedRoutes } from "./Route/Routes";

// Lazy load Dashboard component
const Dashboard = lazy(() => import("./Pages/Dashboard"));

const theme = createTheme({
  colors: {
    primary: colorsTuple("#030406"),
  },
  primaryColor: "primary",
});

const queryClient = new QueryClient();

// Loading component for suspense fallbacks
const LoadingSpinner = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '200px',
    fontSize: '16px'
  }}>
    Loading...
  </div>
);

const App = () => {
  return (
    <div className="App">
      <CustomizerProvider>
        <AnimationThemeProvider>
          <MantineProvider theme={theme}>
            <QueryClientProvider client={queryClient}>
              <RecoilRoot>
                <Toaster />
                <BrowserRouter>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route
                      path="/"
                      element={
                        <AuthMiddleware>
                          <Layout />
                        </AuthMiddleware>
                      }
                    >
                      <Route
                        index
                        element={<Navigate to="/dashboard" replace />}
                      />
                      <Route 
                        path="dashboard" 
                        element={
                          <Suspense fallback={<LoadingSpinner />}>
                            <Dashboard />
                          </Suspense>
                        } 
                      />
                      {authenticatedRoutes.map(({ path, Component }, i) => (
                        <Route 
                          path={path} 
                          element={
                            <Suspense fallback={<LoadingSpinner />}>
                              {Component}
                            </Suspense>
                          } 
                          key={i} 
                        />
                      ))}
                      {/* Add other protected routes here */}
                    </Route>
                  </Routes>
                </BrowserRouter>
              </RecoilRoot>
            </QueryClientProvider>
          </MantineProvider>
        </AnimationThemeProvider>
      </CustomizerProvider>
    </div>
  );
};

export default App;
